import autobind from "class-autobind"
import map from "ramda/src/map"
import pick from "ramda/src/pick"
import merge from "ramda/src/merge"
import omit from "ramda/src/omit"
import is from "ramda/src/is"
import prop from "ramda/src/prop"
import allPass from "ramda/src/allPass"
import test from "ramda/src/test"
import all from "ramda/src/all"
import when from "ramda/src/when"
import identity from "ramda/src/identity"
import getPath from "ramda/src/path"
import objOf from "ramda/src/objOf"
import {noop, bytes, updateIn, toSnake, replaceValues, doPipe} from "hurdak/src/core"
import {Dt} from "utils/dt"
import {poll} from "utils/misc"
import Files from "utils/files"
import {getClient} from "modules/client"

const emptyStringToNull = replaceValues(allPass([is(String), test(/^ *$/)]), null)

export const modifyWhere = ({conditions, field, ..._where}) => {
  return conditions
    ? {..._where, conditions: conditions.map(modifyWhere)}
    : {..._where, field: toSnake(field)}
}

const select = (field, label) => ({field, label})

const where = (field, operator, value = null) => {
  // Identify date filters and format them
  if (is(Dt, value)) {
    value = value.format()
  } else if (operator === "between" && all(is(Dt), value)) {
    value = value.map(dt => dt.format())
  }

  return {field, operator, value}
}

const or = conditions => ({operator: "or", conditions})

const orderBy = (field, order = "DESC") => ({field, order})

class Endpoint {
  base = null
  readOnlyAttrs = ["id", "eType", "created", "deleted"]
  createOnlyAttrs = []
  updateOnlyAttrs = []
  defaultParams = {
    limit: 20,
    offset: 0,
    where: [],
    orderBy: [orderBy("created")],
    select: ["*"],
  }

  constructor({readOnlyAttrs = [], ...opts} = {}) {
    Object.assign(this, {
      readOnlyAttrs: opts.readOnlyAttrsOverride || this.readOnlyAttrs.concat(readOnlyAttrs),
      ...opts,
    })

    autobind(this)
  }

  // Utilities

  url(...parts) {
    const path = [this.base, ...parts].filter(identity).join("/")

    return `/${path}/`
  }

  // General purpose api methods

  req(method, path, {ret = ["data"], ...opts} = {}) {
    return dispatch =>
      dispatch(getClient()[method](this.url(path), opts)).then(getPath(ret), e =>
        Promise.reject(e.errors || e),
      )
  }
  get(path, opts = {}) {
    return this.req("get", path, opts)
  }
  put(path, body = {}, opts = {}) {
    return this.req("put", path, {...opts, body})
  }
  patch(path, body = {}, opts = {}) {
    return this.req("patch", path, {...opts, body})
  }
  post(path, body = {}, opts = {}) {
    return this.req("post", path, {...opts, body})
  }
  delete(path, opts = {}) {
    return this.req("del", path, opts)
  }

  // Special purpose api methods

  list(opts = {}) {
    return this.get(null, opts)
  }
  detail(id, opts = {}) {
    return this.get(id, opts)
  }
  create(data, opts = {}) {
    return this.post(
      null,
      doPipe(data, [omit(this.readOnlyAttrs), omit(this.updateOnlyAttrs), emptyStringToNull]),
    )
  }
  update(id, data, opts = {}) {
    return this.put(
      id,
      doPipe(data, [omit(this.readOnlyAttrs), omit(this.createOnlyAttrs), emptyStringToNull]),
    )
  }
  updateMulti({query, updates, actions}, opts = {}) {
    actions = actions.map(emptyStringToNull)

    updates = doPipe(updates, [
      omit(this.readOnlyAttrs),
      omit(this.createOnlyAttrs),
      emptyStringToNull,
    ])

    return this.patch(null, {
      query: updateIn("where", map(modifyWhere), query),
      updates,
      actions,
    })
  }
}

const accountEndpoint = new Endpoint({
  base: "contact",
  readOnlyAttrs: [
    "isCustomer",
    "numberOfSales",
    "lastItemEntered",
    "lastActivity",
    "shopifyCustomer",
    "balance",
    "numberOfItems",
    "lastSettled",
    "lastViewed",
    "portalAccount",
    "name",
    "active",
    "expired",
    "createdBy",
  ],
})

export default {
  select,
  where,
  or,
  orderBy,
  account: accountEndpoint,
  contact: accountEndpoint,
  artifact: new Endpoint({base: "artifact"}),
  balanceEntry: new Endpoint({
    base: "balance_entry",
    readOnlyAttrsOverride: [
      "id",
      "deleted",
      "eType",
      "idx",
      "tillReport",
      "settlement",
      "contact",
      "item",
      "event",
      "balance",
      "reason",
      "invoice",
      "syncedToPortal",
    ],
  }),
  billing: new Endpoint({base: "billing"}),
  category: new Endpoint({base: "category"}),
  discount: new Endpoint({base: "discount"}),
  emailTemplate: new Endpoint({
    base: "email_template",
    download(name) {
      return getClient().get(this.url(name, "download"), {
        headers: {Accept: "text/plain"},
      })
    },
    preview(name, data = {}) {
      return getClient().post(this.url(name, "preview"), {
        body: data,
        headers: {Accept: "text/html"},
      })
    },
  }),
  event: new Endpoint({base: "event"}),
  giftCard: new Endpoint({
    base: "gift_card",
    readOnlyAttrs: ["history"],
    createOnlyAttrs: ["barcode"],
  }),
  item: new Endpoint({
    base: "item",
    readOnlyAttrs: [
      "consignorPortions",
      "cost",
      "deleted",
      "lastSold",
      "printed",
      "receiptPrices",
      "returnedIn",
      "soldIn",
      "salePrices",
      "shopifyProduct",
      "shopifyVariant",
      "splitPrices",
      "status",
      "storePortions",
      "store",
      "lastViewed",
      "portalItem",
      "notifiedReceived",
      "expired",
      "active",
      "tracked",
      "syncedToNearst",
      "daysOnShelf",
      "titleIsSet",
      "createdBy",
    ],
  }),
  itemField: new Endpoint({base: "item_field"}),
  statusChange: new Endpoint({base: "status_change"}),
  job: new Endpoint({
    base: "job",
    monitorProgress(opts = {}) {
      return async dispatch => {
        opts = {onProgress: noop, onFailure: noop, onCompletion: noop, ...opts}

        const promise = poll({
          makeRequest: async () => {
            const data = await dispatch(this.detail(opts.jobId))

            dispatch(opts.onProgress(data))

            return data
          },
          isComplete: prop("completed"),
          isError: prop("failed"),
        })

        try {
          return opts.onCompletion(await promise)
        } catch (data) {
          throw opts.onFailure(data)
        }
      }
    },
  }),
  label: new Endpoint({base: "label"}),
  lineItem: new Endpoint({base: "line_item"}),
  location: new Endpoint({base: "location"}),
  note: new Endpoint({base: "note", readOnlyAttrs: ["user"]}),
  payment: new Endpoint({base: "payment"}),
  portal: new Endpoint({base: "portal"}),
  refund: new Endpoint({base: "refund"}),
  report: new Endpoint({base: "report"}),
  sale: new Endpoint({base: "transaction"}),
  search: new Endpoint({
    base: "search",
    defaultParams: {
      limit: 20,
      offset: 0,
      where: [],
      orderBy: [],
      select: ["*"],
    },
    entity(entityType, params, opts = {}) {
      return this.post(
        toSnake(entityType),
        doPipe(params, [
          when(is(Array), objOf("where")),
          merge(this.defaultParams),
          updateIn("where", map(modifyWhere)),
          updateIn("select", map(toSnake)),
          updateIn("orderBy", map(updateIn("field", toSnake))),
        ]),
        {ret: [], ...opts},
      )
    },
    entityExport(entityType, params) {
      return this.post(
        `${toSnake(entityType)}/export`,
        doPipe(params, [
          when(is(Array), objOf("where")),
          merge(pick(["where"], this.defaultParams)),
          // Select here is export select, so {field, label}
          updateIn("where", map(modifyWhere)),
          updateIn("select", map(updateIn("field", toSnake))),
          updateIn("orderBy", map(updateIn("field", toSnake))),
        ]),
      )
    },
  }),
  session: new Endpoint({base: "session"}),
  setting: new Endpoint({base: "setting"}),
  shelf: new Endpoint({base: "shelf"}),
  shopify: new Endpoint({base: "shopify"}),
  store: new Endpoint({
    base: "store",
    createOnlyAttrs: ["referral", "acceptedPrivacy", "acceptedTerms", "coupon", "email"],
    readOnlyAttrs: [
      "onboarding",
      "apiKey",
      "emailVerified",
      "emailVerificationCode",
      "passwordHash",
      "passwordResetCode",
      "passwordResetExpires",
      "schema",
      "stripeId",
      "auth0Id",
      "canceled",
      "shopifyActive",
      "shopifyShop",
      "shopifyAccessToken",
      "modules",
      "portalAccessToken",
      "portalSharedSecret",
      "creditCardImplementation",
      "emergepayConfig",
      "proPayAccountNumber",
      "proPayStatus",
      "proPayTier",
      "protectPayMerchantProfile",
      "protectPayPayer",
      "anywhereCommerceTerminals",
      "printnodeKey",
      "printnodeEmail",
      "printnodeAccount",
      "printnodePassword",
      "lastReceivedNotification",
      "billingBackend",
      "lastReturnedNotification",
      "lastSoldNotification",
      "status",
      "shopifyCharge",
      "tenant",
      "emergepayOid",
      "emergepayDevices",
      "freeModules",
      "portalLinked",
      "shopifyLinked",
      "active",
      "portalId",
    ],
  }),
  surcharge: new Endpoint({base: "surcharge"}),
  swipe: new Endpoint({base: "swipe"}),
  tax: new Endpoint({base: "tax"}),
  till: new Endpoint({base: "till"}),
  tillReport: new Endpoint({base: "till_report"}),
  tillReportEntry: new Endpoint({base: "till_report_entry"}),
  upload: new Endpoint({
    base: "upload",
    deref(id) {
      return dispatch => (id ? dispatch(this.get(id)) : null)
    },
    contents(id) {
      return async dispatch => {
        const {url} = await dispatch(this.get(id))
        const res = await window.fetch(url)

        return res.text()
      }
    },
    save({uploadId, fileKey, fileNs, resize, formKey, limit = bytes(5, "mb")}) {
      return async dispatch => {
        // If it's not a file object, but it is an object, it's an upload object, no change
        if (!is(File, fileKey) && is(Object, fileKey)) {
          return uploadId
        }

        // Check if they are deleting the image
        if (!fileKey) {
          return null
        }

        const fileObj = is(File, fileKey) ? fileKey : Files.getFile(fileNs, fileKey)

        // If no file object, we're clearing out the upload.
        if (!fileObj) {
          return null
        }

        // Validate the file itself
        if (fileObj.size > limit) {
          return Promise.reject([
            {
              code: "file_size_limit_exceeded",
              description: "The selected file is too large.",
              path: [formKey],
            },
          ])
        }

        const url = uploadId ? `/upload/${uploadId}/` : "/upload/"
        const method = uploadId ? getClient().put : getClient().post
        const formData = new FormData()

        formData.append("file", fileObj)

        if (resize) {
          formData.append("resize", resize)
        }

        const {
          data: {id},
        } = await dispatch(
          method(url, {
            body: formData,
            headers: {
              "Content-Type": false,
              Accept: "application/json",
            },
          }),
        )

        return id
      }
    },
  }),
  user: new Endpoint({
    base: "user",
    readOnlyAttrs: ["isOwner", "passwordIsSet", "intercomHash"],
  }),
}
